import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faChevronRight,
  faChevronDown,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { Badge, Button, Form, InputGroup } from "@themesberg/react-bootstrap";

import EmptyState from "../dashboard/EmptyState";
import { buildHierarchy, getLevelBadge, formatDate, PAGE_SIZE } from "./networkHelpers";

function TreeNode({
  node,
  depth = 0,
  maxInitialDepth = 2,
  expandedNodes,
  onToggle,
  focusUserId,
}) {
  const hasChildren = node.children?.length > 0;
  const isExpanded = expandedNodes[node.id] ?? depth < maxInitialDepth;
  const isFocused = focusUserId && node.id === focusUserId;

  return (
    <div className={depth > 0 ? "network-tree-children" : ""}>
      <div
        className={`network-tree-node ${isFocused ? "border-primary" : ""}`}
        style={isFocused ? { boxShadow: "0 0 0 2px rgba(37,99,235,0.25)" } : undefined}
      >
        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            {hasChildren ? (
              <Button
                size="sm"
                variant="outline-primary"
                className="py-0 px-2"
                onClick={() => onToggle(node.id)}
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
              </Button>
            ) : (
              <span style={{ width: 32 }} />
            )}
            <div>
              <div className="fw-semibold">{node.name}</div>
              <div className="small text-muted">
                {node.customUserId} · Joined {formatDate(node.createdAt)}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Badge bg={getLevelBadge(node.level).bg} className="rounded-pill px-2 py-1">
              {getLevelBadge(node.level).label}
            </Badge>
            {hasChildren && (
              <span className="small text-muted">{node.children.length} direct</span>
            )}
          </div>
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div className="mt-1">
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              maxInitialDepth={maxInitialDepth}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
              focusUserId={focusUserId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TreeViewTab({
  treeFlat = [],
  rootUser = {},
  loading = false,
  focusUserId = null,
  search: externalSearch = "",
}) {
  const [search, setSearch] = useState(externalSearch);
  const [expandedNodes, setExpandedNodes] = useState({});

  useEffect(() => {
    if (externalSearch) setSearch(externalSearch);
  }, [externalSearch]);

  useEffect(() => {
    if (!focusUserId || !treeFlat.length) return;

    const path = new Map();
    treeFlat.forEach((n) => path.set(n.id, n.referredBy));

    const expandIds = {};
    let current = focusUserId;
    while (current != null) {
      expandIds[current] = true;
      current = path.get(current);
    }
    setExpandedNodes((prev) => ({ ...prev, ...expandIds }));
  }, [focusUserId, treeFlat]);

  const filteredFlat = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return treeFlat;
    return treeFlat.filter(
      (n) =>
        String(n.name || "").toLowerCase().includes(q) ||
        String(n.id || "").includes(q)
    );
  }, [treeFlat, search]);

  const hierarchy = useMemo(() => buildHierarchy(filteredFlat), [filteredFlat]);

  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));
  };

  const expandAllDirect = () => {
    const next = {};
    hierarchy.forEach((n) => {
      next[n.id] = true;
    });
    setExpandedNodes(next);
  };

  if (loading) {
    return <div className="text-center py-5 text-muted">Loading tree...</div>;
  }

  if (treeFlat.length === 0) {
    return (
      <EmptyState
        icon={faUsers}
        title="No network tree yet"
        message="Your downline tree will appear here once you have referrals."
      />
    );
  }

  return (
    <>
      <div className="network-filter-bar">
        <InputGroup style={{ maxWidth: 320, flex: "1 1 200px" }}>
          <InputGroup.Text className="bg-white">
            <FontAwesomeIcon icon={faSearch} />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search by name or user ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        <Button variant="outline-primary" size="sm" onClick={expandAllDirect}>
          Expand Direct Referrals
        </Button>
      </div>

      <div className="network-tree-root">
        <div className="fw-bold">{rootUser.name || "You"}</div>
        <div className="small opacity-75">
          {rootUser.userId ? `ID: ${rootUser.userId}` : "Root user"} · Your network starts here
        </div>
      </div>

      {hierarchy.length === 0 ? (
        <div className="text-muted text-center py-4">No matches for your search.</div>
      ) : (
        <>
          {/* {hierarchy.slice(0, PAGE_SIZE).map((node) => ( */}
          {hierarchy.map((node) => ( 
            <TreeNode
              key={node.id}
              node={node}
              depth={0}
              expandedNodes={expandedNodes}
              onToggle={toggleNode}
              focusUserId={focusUserId}
            />
          ))}
          {/* {hierarchy.length > PAGE_SIZE && (
            <div className="text-center text-muted small mt-2">
              Showing first {PAGE_SIZE} of {hierarchy.length} direct referrals. Use search to find others.
            </div>
          )} */}
        </>
      )}
    </>
  );
}
